"use client";

/**
 * FollowConfirmPopup
 *
 * Rich centered popup shown after successfully following a user or artist.
 * Displays their avatar, name, bio, follower/following counts, and a few
 * linked content snippets. Auto-closes after AUTO_CLOSE_MS with a
 * countdown progress bar. Clicking anywhere on the card navigates to
 * their profile page.
 */

import { useEffect, useState, useRef } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { X, UserCheck, Users, Music, ShoppingBag, Play, ChevronRight } from "lucide-react";

const AUTO_CLOSE_MS = 6000;

export interface FollowConfirmData {
  /** Profile/artist page ID */
  targetId: string;
  /** Display name */
  targetName: string;
  /** Profile image URL */
  targetImageUrl?: string;
  /** Short bio text */
  bio?: string;
  /** Follower count */
  followerCount?: number;
  /** Following count */
  followingCount?: number;
  /** Account or entity type label shown as badge */
  typeLabel?: string;
  /** Navigation href — e.g. /artists/slug or /profile/uid */
  profileLink: string;
  /** Optional snippet items (up to 4 shown) */
  snippets?: FollowSnippet[];
}

export interface FollowSnippet {
  id: string;
  title: string;
  imageUrl?: string;
  /** "track" | "release" | "product" | "playlist" */
  type: "track" | "release" | "product" | "playlist";
}

interface FollowConfirmPopupProps {
  data: FollowConfirmData;
  onClose: () => void;
}

export function FollowConfirmPopup({ data, onClose }: FollowConfirmPopupProps) {
  const router = useRouter();
  const [progress, setProgress] = useState(100);
  const startedAt = useRef(Date.now());
  const animRef = useRef(0);
  const [exiting, setExiting] = useState(false);

  // Countdown progress bar + auto-close
  useEffect(() => {
    const tick = () => {
      const elapsed = Date.now() - startedAt.current;
      const remaining = Math.max(0, 1 - elapsed / AUTO_CLOSE_MS);
      setProgress(remaining * 100);
      if (elapsed >= AUTO_CLOSE_MS) {
        handleClose();
      } else {
        animRef.current = requestAnimationFrame(tick);
      }
    };
    animRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(animRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    if (exiting) return;
    setExiting(true);
    setTimeout(onClose, 280);
  };

  const handleNavigate = () => {
    handleClose();
    setTimeout(() => router.push(data.profileLink), 80);
  };

  const snippets = (data.snippets || []).slice(0, 4);

  const typeIcon = (type: FollowSnippet["type"]) => {
    switch (type) {
      case "track":    return <Play className="h-3 w-3 text-primary/70" />;
      case "release":  return <Music className="h-3 w-3 text-primary/70" />;
      case "product":  return <ShoppingBag className="h-3 w-3 text-primary/70" />;
      case "playlist": return <Music className="h-3 w-3 text-primary/70" />;
    }
  };

  return (
    <>
      <style>{`
        @keyframes follow-popup-in {
          from { opacity: 0; transform: translate(-50%, -48%) scale(0.93); }
          to   { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes follow-popup-out {
          from { opacity: 1; transform: translate(-50%, -50%) scale(1); }
          to   { opacity: 0; transform: translate(-50%, -52%) scale(0.93); }
        }
        @keyframes follow-avatar-ring {
          0%,100% { box-shadow: 0 0 0 3px hsl(var(--primary)/0.25); }
          50%     { box-shadow: 0 0 0 6px hsl(var(--primary)/0.50); }
        }
      `}</style>

      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[300] bg-black/40 backdrop-blur-[2px]"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Card */}
      <div
        className="fixed z-[301] left-1/2 top-1/2 w-[min(92vw,380px)]"
        style={{
          animation: exiting
            ? "follow-popup-out 280ms cubic-bezier(0.4,0,1,1) forwards"
            : "follow-popup-in 360ms cubic-bezier(0.22,1,0.36,1) forwards",
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-background border border-border rounded-3xl shadow-2xl overflow-hidden">

          {/* Auto-close progress bar */}
          <div className="h-1 bg-muted w-full">
            <div
              className="h-full bg-primary transition-none rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Content */}
          <div className="p-5">
            {/* Header row */}
            <div className="flex items-start gap-3 mb-4">
              {/* Avatar */}
              <button
                onClick={handleNavigate}
                className="flex-shrink-0 relative"
                aria-label={`View ${data.targetName}`}
              >
                <div
                  className="w-16 h-16 rounded-2xl overflow-hidden bg-muted border-2 border-primary/30"
                  style={{ animation: "follow-avatar-ring 2.4s ease-in-out infinite" }}
                >
                  {data.targetImageUrl ? (
                    <Image
                      src={data.targetImageUrl}
                      alt={data.targetName}
                      width={64}
                      height={64}
                      className="w-full h-full object-cover"
                     
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-primary/10">
                      <span className="text-xl font-bold text-primary">
                        {data.targetName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                {/* "Following" badge overlay */}
                <span className="absolute -bottom-1.5 -right-1.5 flex items-center justify-center w-6 h-6 rounded-full bg-primary border-2 border-background">
                  <UserCheck className="h-3 w-3 text-primary-foreground" />
                </span>
              </button>

              {/* Name + type badge + bio */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={handleNavigate}
                    className="text-base font-bold text-foreground hover:text-primary transition-colors leading-tight text-left"
                  >
                    {data.targetName}
                  </button>
                  {data.typeLabel && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/20">
                      {data.typeLabel}
                    </span>
                  )}
                </div>

                {/* Following confirmation text */}
                <p className="text-xs text-primary font-medium mt-0.5">You&apos;re now following!</p>

                {/* Bio */}
                {data.bio && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-snug">
                    {data.bio}
                  </p>
                )}
              </div>

              {/* Close */}
              <button
                onClick={handleClose}
                className="flex-shrink-0 p-1 -m-1 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Stats row */}
            {(data.followerCount !== undefined || data.followingCount !== undefined) && (
              <div className="flex items-center gap-4 mb-4 py-2.5 px-3 rounded-xl bg-muted/50 border border-border/50">
                <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                {data.followerCount !== undefined && (
                  <span className="text-xs text-foreground">
                    <strong className="font-semibold">{formatCount(data.followerCount + 1)}</strong>
                    <span className="text-muted-foreground ml-1">followers</span>
                  </span>
                )}
                {data.followerCount !== undefined && data.followingCount !== undefined && (
                  <span className="text-muted-foreground/40 text-xs">·</span>
                )}
                {data.followingCount !== undefined && (
                  <span className="text-xs text-foreground">
                    <strong className="font-semibold">{formatCount(data.followingCount)}</strong>
                    <span className="text-muted-foreground ml-1">following</span>
                  </span>
                )}
              </div>
            )}

            {/* Content snippets */}
            {snippets.length > 0 && (
              <div className="mb-4">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-1.5">
                  Their content
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {snippets.map(s => (
                    <div
                      key={s.id}
                      className="flex items-center gap-2 p-1.5 rounded-lg bg-muted/40 border border-border/40"
                    >
                      {s.imageUrl ? (
                        <div className="w-8 h-8 rounded-md overflow-hidden flex-shrink-0 bg-muted">
                          {/* eslint-disable-next-line @next/next/no-img-element */}
                          <img src={s.imageUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {typeIcon(s.type)}
                        </div>
                      )}
                      <span className="text-[11px] text-foreground font-medium truncate leading-tight">
                        {s.title}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA button */}
            <button
              onClick={handleNavigate}
              className="w-full flex items-center justify-center gap-2 h-10 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors"
            >
              View Profile
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000)     return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}
